import { useEffect, useMemo } from "react";
import EditMedals from "../components/EditMedals";
import EditStats from "../components/EditStats";
import { z } from "zod";
import useRegisterUserMedal from "../hooks/useRegisterUserMedal";
import useUserStore from "../store/userStore";
import UserMedal from "../interfaces/userMedal";
import Medal from "../interfaces/medal";
import { useNavigate } from "react-router-dom";
import useEditPokemonGoProfile from "../hooks/useEditPokemonGoProfile";
import useUser from "../hooks/useUser";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

export type ProfileFormData = z.infer<ReturnType<typeof createProfileSchema>>;

const createProfileSchema = (
  originalXp: number,
  originalLevel: number,
  originalMedalsMap: Map<number, number>
) =>
  z.object({
    stats: z
      .object({
        xp: z.number().int().min(originalXp, { message: "XP cannot be lower than previous xp" }),
        level: z.number().int().min(originalLevel, { message: "Level cannot be lower than previous level" }),
        team: z.number().int().min(1, { message: "Team must be selected" }),
      }),
    medals: z.array(
      z
        .object({
          quantity: z
            .number({ required_error: "Quantity is required" })
            .min(0, "Quantity cannot be negative")
            .default(0),
          medal: z.object({
            id: z.number(),
          }),
        })
        .refine(
          (data) => {
            const originalQuantity = originalMedalsMap.get(data.medal.id) ?? 0;
            return data.quantity >= originalQuantity;
          },
          (data) => ({
            message: `Quantity cannot be lower than the previous value of ${
              originalMedalsMap.get(data.medal.id) ?? 0
            }.`,
            path: ["quantity"],
          })
        )
    ),
  });

function EditProfile() {
  const navigate = useNavigate();
  const userToken = useUserStore((state) => state.token);
  const username = useUserStore((state) => state.username);

  const { data: userData, isLoading: isUserLoading } = useUser(username);
  const { mutate: registerUserMedal } = useRegisterUserMedal(userToken);
  const { mutate: saveStats } = useEditPokemonGoProfile(userToken, username);

  
  const profileSchema = useMemo(() => {
    if (userData?.pokemonGoProfile && userData.medals) {
      const originalMedalsMap = new Map<number, number>(
        userData.medals.map((m) => [m.medal.id!, m.quantity])
      );
      return createProfileSchema(
        userData.pokemonGoProfile.xp,
        userData.pokemonGoProfile.level,
        originalMedalsMap
      );
    }
    return z.object({
      stats: z.object({ xp: z.number(), level: z.number(), team: z.number() }),
      medals: z.array(z.object({ quantity: z.number(), medal: z.object({ id: z.number() }) }))
    });
  }, [userData])

  const methods = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  });

  useEffect(() => {
    if (userData?.pokemonGoProfile && userData.medals) {
      methods.reset({
        stats: {
          xp: userData.pokemonGoProfile.xp,
          level: userData.pokemonGoProfile.level,
          team:
            typeof userData.pokemonGoProfile.team === "object"
              ? userData.pokemonGoProfile.team.id
              : userData.pokemonGoProfile.team,
        },
        medals: userData.medals.map((userMedal) => ({
          quantity: userMedal.quantity,
          medal: {
            id: userMedal.medal.id,
          },
        })),
      });
    }
  }, [userData, userData?.medals, methods]);

  const handleOverallSave = (data: ProfileFormData) => {
    if (data.stats) {
      saveStats(data.stats);
    }

    if (data.medals) {
      const medalsToMutate: UserMedal[] = data.medals.map((item) => ({
        quantity: item.quantity,
        createdAt: new Date(),
        medal: item.medal as Medal,
      }));

      registerUserMedal(medalsToMutate, {
        onSuccess: () => {
          navigate("/profile");
        },
      });
    }
  };

  if (isUserLoading) {
    return (
      <div className="text-center p-5">Loading profile for editing...</div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(handleOverallSave)}
        className="container mt-4 mb-5"
        style={{ color: "#c2c2c2" }}
      >
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div
              className="card p-4 mb-4 shadow-sm"
              style={{
                backgroundColor: "#2a2d2a",
                border: "1px solid #3c403c",
              }}
            >
              <EditStats />
            </div>
            <div
              className="card p-4 mb-4 shadow-sm"
              style={{
                backgroundColor: "#2a2d2a",
                border: "1px solid #3c403c",
              }}
            >
              <EditMedals data={userData?.medals!}/>
            </div>
            <div className="d-grid mt-4">
              <button
                type="submit"
                className="btn btn-lg"
                style={{
                  backgroundColor: "#5cb85c",
                  borderColor: "#5cb85c",
                  color: "#1d1f1c",
                }}
              >
                Save All Changes
              </button>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

export default EditProfile;
